import React, { useState, useMemo, useEffect, Fragment } from 'react'
import { Col } from 'react-bootstrap'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
// import cn from 'classnames'

import { escapeCsvString } from '../../../../shared/csv'

import { withStyles } from '@material-ui/core'
import Card from '@material-ui/core/Card'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Box from '@material-ui/core/Box'
import BounceLoader from 'Components/BounceLoader'

import { TITLES, DEFAULT_SELECTED_METRICS, CSV_HEADERS } from '../../constants'

import { setCampaignsTotals, getMetricsCount } from './utils'
import { getReadableStatus } from '../../../../Containers/Campaigns/utils'

import style from './style.js'

const CampaignTotals = ({
  classes,
  campaignsTotalCount,
  campaignCalculatedData,
  campaignsList,
  setExportContent,
}) => {
  const [metricOptions, setMetricOptions] = useState([])
  const [selectedMetrics, setSelectedMetrics] = useState(
    DEFAULT_SELECTED_METRICS,
  )
  const metricsCount = useMemo(() => {
    return getMetricsCount(campaignCalculatedData)
  }, [campaignCalculatedData])

  const currencySign = useMemo(() => {
    const campaignWithCurrency = campaignsList.find(c => !!c.currencySign)
    return campaignWithCurrency ? campaignWithCurrency.currencySign : ''
  }, [campaignsList])

  const isLoading = useMemo(() => {
    return (
      (campaignsTotalCount !== 0 && !campaignsTotalCount) ||
      campaignCalculatedData.length < campaignsTotalCount
    )
  }, [campaignsTotalCount, campaignCalculatedData])

  const campaignCalculatedDataTotals = useMemo(
    () =>
      campaignCalculatedData.reduce(
        (acc, campaign) => {
          return setCampaignsTotals(campaign, acc, metricsCount)
        },
        {
          totalFlyersDistributed: 0,
          totalHardCosts: 0,
          totalScans: 0,
          totalPrintingCost: 0,
          totalPermitCost: 0,
          totalDistributionCost: 0,
          totalBonusCost: 0,
          totalMissionsCount: 0,
        },
      ),
    [campaignCalculatedData],
  )

  const getMetricOptions = () => {
    return [
      ...campaignCalculatedData.reduce((acc, { metrics }) => {
        metrics.forEach(metric => acc.add(metric.label || metric.name))

        return acc
      }, new Set([])),
    ].reduce((acc, metric) => {
      const formattedMetricName = metric.split(' ').join('')
      return [
        ...acc,
        ...[
          { key: `total${formattedMetricName}`, label: metric },
          {
            key: `totalCostPer${formattedMetricName}`,
            label: `Cost Per ${metric}`,
          },
          { key: `total${formattedMetricName}CVR`, label: `${metric} CVR` },
        ],
      ]
    }, [])
  }

  useEffect(() => {
    if (campaignCalculatedData.length) {
      const nonDefaultSelectedOptions = selectedMetrics.reduce(
        (acc, metric) => {
          if (!DEFAULT_SELECTED_METRICS.includes(metric)) {
            if (metric.startsWith('totalCostPer')) {
              return [
                ...acc,
                {
                  key: metric,
                  label: `Cost Per ${metric.split('totalCostPer')[1]}`,
                },
              ]
            } else if (metric.includes('CVR')) {
              return [
                ...acc,
                {
                  key: metric,
                  label: `${metric.match('total(.*)CVR')[1]} CVR`,
                },
              ]
            } else {
              return [...acc, { key: metric, label: metric.split('total')[1] }]
            }
          }
          return acc
        },
        [],
      )

      const newMetricOptions = [
        ...nonDefaultSelectedOptions,
        ...getMetricOptions(),
      ].filter(
        (value, index, arr) =>
          arr.findIndex(item => item.key === value.key) === index,
      )

      setMetricOptions(newMetricOptions)
    }
  }, [campaignCalculatedData])

  useEffect(() => {
    if (!setExportContent) return

    const headers = [
      ...Object.values(CSV_HEADERS),
      ...metricOptions.map(metric => metric.label),
    ]
    const valuesKeys = [
      ...Object.keys(CSV_HEADERS),
      ...metricOptions.map(metric => metric.key),
    ]
    const content =
      headers.join(',') +
      '\n' +
      campaignCalculatedData
        .map(campaign => {
          return valuesKeys
            .map(value => {
              if (value === 'missions') {
                return escapeCsvString(
                  campaign[value].length.toLocaleString('en'),
                )
              }
              if (value === 'readableStatus') {
                const campaignSimpleData = campaignsList.find(
                  c => c.id === campaign.id,
                )
                if (campaignSimpleData) {
                  return getReadableStatus(campaignSimpleData.status)
                }
              } else if (campaign[value] || campaign[value] === 0) {
                return typeof campaign[value] === 'string'
                  ? escapeCsvString(campaign[value])
                  : escapeCsvString(
                      Number(campaign[value].toFixed(2)).toLocaleString('en'),
                    )
              } else if (campaign.costs[value] || campaign.costs[value] === 0) {
                return escapeCsvString(
                  Number(campaign.costs[value].toFixed(2)).toLocaleString('en'),
                )
              } else if (value.includes('total')) {
                let selectedMetric = {}

                if (value.startsWith('totalCostPer')) {
                  selectedMetric =
                    campaign.metrics.find(metric =>
                      metric.label
                        ? metric.label.split(' ').join('') ===
                          value.split('totalCostPer')[1]
                        : metric.name.split(' ').join('') ===
                          value.split('totalCostPer')[1],
                    ) || {}

                  if (Object.keys(selectedMetric).length) {
                    return escapeCsvString(
                      Number(
                        selectedMetric.costPerMetric.toFixed(2),
                      ).toLocaleString('en'),
                    )
                  }
                  return '-'
                } else if (value.includes('CVR')) {
                  selectedMetric =
                    campaign.metrics.find(metric =>
                      metric.label
                        ? metric.label.split(' ').join('') ===
                          value.match('total(.*)CVR')[1]
                        : metric.name.split(' ').join('') ===
                          value.match('total(.*)CVR')[1],
                    ) || {}

                  if (Object.keys(selectedMetric).length) {
                    return escapeCsvString(
                      Number(
                        selectedMetric.conversionRate.toFixed(2),
                      ).toLocaleString('en'),
                    )
                  }
                  return '-'
                } else {
                  selectedMetric =
                    campaign.metrics.find(metric =>
                      metric.label
                        ? metric.label.split(' ').join('') ===
                          value.split('total')[1]
                        : metric.name.split(' ').join('') ===
                          value.split('total')[1],
                    ) || {}
                  if (Object.keys(selectedMetric).length) {
                    return escapeCsvString(
                      Number(
                        selectedMetric.metricValue.toFixed(2),
                      ).toLocaleString('en'),
                    )
                  }

                  return '-'
                }
              }
              return '-'
            })
            .join(',')
        })
        .join(',\n')

    setExportContent(content)
  }, [campaignCalculatedData, metricOptions, setExportContent, campaignsList])

  const options = [
    ...Object.entries(TITLES).reduce(
      (acc, [key, value]) =>
        !selectedMetrics.includes(key) ? [...acc, { key, label: value }] : acc,
      [],
    ),
    ...metricOptions.filter(({ key }) => !selectedMetrics.includes(key)),
  ].sort((a, b) => a.label.localeCompare(b.label))

  const getPreffix = key => {
    if (key && key.toLowerCase().includes('cost')) {
      return `${currencySign} `
    }
    return null
  }

  const getSuffix = key => {
    if (key && key.toLowerCase().includes('cvr')) {
      return ' %'
    }
    return null
  }

  const getMetricValue = (key, value) => {
    if (key && key.toLowerCase().includes('cvr')) {
      if (Number(value) >= 100) {
        return 100
      } else {
        return Number(value).toFixed(2)
      }
    } else if (key && key.toLowerCase().includes('cost')) {
      return value.toLocaleString('en', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    } else {
      return value.toLocaleString('en')
    }
  }

  return (
    <Box className={classes.totalsWrapper}>
      {selectedMetrics.map((key, index) => (
        <Col key={`${key}-${index}-col`} className={classes.totalsCardWrapper}>
          <Card key={`${key}-${index}-card`} className={classes.card}>
            <div>
              <Select
                className={classes.cardTitle}
                value={key}
                IconComponent={KeyboardArrowDownIcon}
                disableUnderline
                onChange={({ target }) =>
                  setSelectedMetrics(
                    selectedMetrics.map(metric =>
                      metric === key ? target.value : metric,
                    ),
                  )
                }
              >
                {[
                  {
                    key,
                    label:
                      TITLES[key] ||
                      metricOptions.find(metric => metric.key === key).label,
                  },
                  ...options,
                ].map(({ key, label }) => (
                  <MenuItem key={`${key}-${index}-menuItem`} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div>
              <Col className={classes.cardValue}>
                {isLoading ? (
                  <BounceLoader color={'#ffffff'} />
                ) : campaignCalculatedDataTotals[key] ? (
                  <Fragment>
                    <span>{getPreffix(key)}</span>
                    <span>
                      {getMetricValue(key, campaignCalculatedDataTotals[key])}
                    </span>
                    <span>{getSuffix(key)}</span>
                  </Fragment>
                ) : (
                  <Fragment>
                    <span>{getPreffix(key)}</span>
                    <span>{0}</span>
                    <span>{getSuffix(key)}</span>
                  </Fragment>
                )}
              </Col>
            </div>
          </Card>
        </Col>
      ))}
    </Box>
  )
}

export default withStyles(style)(CampaignTotals)
